import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import catalogConfigService from 'services/catalogConfigService';
import branchService from 'services/branchService';
import priceListService from 'services/priceListService';
import { Branch, PriceList } from 'types/storefront';
import { STORAGE_KEYS } from 'constants/storageKeys';
import { tenantStorage } from 'utils/tenantStorage';
import i18n from 'i18n';

interface CatalogPreferencesValue {
  locale: string;
  currency: string;
  branchId: string | null;
  priceListId: string | null;
  branches: Branch[];
  priceLists: PriceList[];
  setLocale: (locale: string) => void;
  setCurrency: (currency: string) => void;
  setBranchId: (id: string) => void;
  setPriceListId: (id: string) => void;
}

const CatalogPreferencesContext = createContext<CatalogPreferencesValue | undefined>(undefined);

export const CatalogPreferencesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [locale, setLocaleState] = useState(catalogConfigService.getDefaultLocale);
  const [currency, setCurrencyState] = useState(catalogConfigService.getDefaultCurrency);
  const [branchId, setBranchIdState] = useState<string | null>(tenantStorage.getItem(STORAGE_KEYS.BRANCH_ID));
  const [priceListId, setPriceListIdState] = useState<string | null>(tenantStorage.getItem(STORAGE_KEYS.PRICE_LIST_ID));
  const [branches, setBranches] = useState<Branch[]>([]);
  const [priceLists, setPriceLists] = useState<PriceList[]>([]);

  useEffect(() => {
    branchService.getBranches().then(setBranches).catch(() => setBranches([]));
    priceListService.getPriceLists().then(setPriceLists).catch(() => setPriceLists([]));
  }, []);

  const setLocale = (value: string) => {
    catalogConfigService.setLocale(value);
    setLocaleState(value);
    i18n.changeLanguage(value);
  };

  const setCurrency = (value: string) => {
    catalogConfigService.setCurrency(value);
    setCurrencyState(value);
  };

  const setBranchId = (id: string) => {
    catalogConfigService.setBranchId(id);
    setBranchIdState(id);
  };

  const setPriceListId = (id: string) => {
    catalogConfigService.setPriceListId(id);
    setPriceListIdState(id);
  };

  const value = useMemo(
    () => ({
      locale,
      currency,
      branchId,
      priceListId,
      branches,
      priceLists,
      setLocale,
      setCurrency,
      setBranchId,
      setPriceListId,
    }),
    [locale, currency, branchId, priceListId, branches, priceLists]
  );

  return (
    <CatalogPreferencesContext.Provider value={value}>{children}</CatalogPreferencesContext.Provider>
  );
};

export const useCatalogPreferencesContext = () => {
  const ctx = useContext(CatalogPreferencesContext);
  if (!ctx) throw new Error('useCatalogPreferencesContext debe usarse dentro de CatalogPreferencesProvider');
  return ctx;
};
